import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { money, timeAgo } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Bus, Trash2, MapPin, Bike } from "lucide-react";

const DSTATUS = {
  assigned: "bg-blue-100 text-blue-700", picked: "bg-yellow-100 text-yellow-800",
  delivered: "bg-green-100 text-green-700",
};

export default function Delivery() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", vehicle: "Bike" });
  const [assignFor, setAssignFor] = useState(null);

  const { data: orders = [] } = useQuery({ queryKey: ["orders"], queryFn: async () => (await api.get("/orders")).data, refetchInterval: 10000 });
  const { data: riders = [] } = useQuery({ queryKey: ["riders"], queryFn: async () => (await api.get("/riders")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const sym = settings?.currency_symbol || "₹";

  const deliveries = orders.filter((o) => o.order_type === "delivery" && o.status !== "cancelled");

  const addRider = async () => {
    if (!form.name || !form.phone) return toast.error("Name & phone required");
    await api.post("/riders", { ...form, status: "available" });
    toast.success("Rider added"); setOpen(false); setForm({ name: "", phone: "", vehicle: "Bike" });
    qc.invalidateQueries({ queryKey: ["riders"] });
  };
  const delRider = async (r) => { await api.delete(`/riders/${r.id}`); qc.invalidateQueries({ queryKey: ["riders"] }); };
  const assign = async (riderId) => {
    await api.post(`/orders/${assignFor.id}/assign-rider`, { rider_id: riderId });
    toast.success("Rider assigned"); setAssignFor(null);
    qc.invalidateQueries({ queryKey: ["orders"] }); qc.invalidateQueries({ queryKey: ["riders"] });
  };
  const setStatus = async (o, delivery_status) => {
    await api.put(`/orders/${o.id}/delivery-status`, { delivery_status });
    toast.success(`Marked ${delivery_status}`);
    qc.invalidateQueries({ queryKey: ["orders"] }); qc.invalidateQueries({ queryKey: ["riders"] });
  };

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8">
      <PageHeader title="Self-Delivery" subtitle="Assign riders and track your own deliveries">
        <Button onClick={() => setOpen(true)} data-testid="add-rider-btn" className="bg-[var(--kul-primary)] text-white"><Plus className="w-4 h-4 mr-1" /> Add Rider</Button>
      </PageHeader>

      <Tabs defaultValue="orders">
        <TabsList><TabsTrigger value="orders" data-testid="tab-deliveries">Deliveries</TabsTrigger><TabsTrigger value="riders" data-testid="tab-riders">Riders</TabsTrigger></TabsList>
        <TabsContent value="orders">
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {deliveries.map((o) => (
              <div key={o.id} className="kul-card p-4" data-testid={`delivery-${o.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono-k font-semibold">{o.order_number}</span>
                  <Badge className={`capitalize ${o.channel !== "pos" && o.channel !== "online" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>{o.channel}</Badge>
                </div>
                <div className="text-sm text-neutral-500 mt-1">{o.customer_name} · {money(o.total, sym)}</div>
                <div className="text-xs text-neutral-400">{timeAgo(o.created_at)}</div>
                <div className="mt-3">
                  {o.delivery_rider ? (
                    <div className="flex items-center gap-2 text-sm"><Bike className="w-4 h-4 text-[var(--kul-primary)]" /> {o.delivery_rider}
                      <Badge className={`ml-auto capitalize ${DSTATUS[o.delivery_status] || "bg-neutral-100"}`}>{o.delivery_status || "assigned"}</Badge>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" className="w-full" onClick={() => setAssignFor(o)} data-testid={`assign-${o.id}`}><MapPin className="w-4 h-4 mr-1" />Assign Rider</Button>
                  )}
                  {o.delivery_rider && o.delivery_status !== "delivered" && (
                    <div className="flex gap-1 mt-2">
                      {o.delivery_status !== "picked" && <Button size="sm" variant="outline" className="flex-1" onClick={() => setStatus(o, "picked")}>Picked up</Button>}
                      <Button size="sm" className="flex-1 bg-[var(--kul-primary)] text-white" onClick={() => setStatus(o, "delivered")} data-testid={`deliver-${o.id}`}>Delivered</Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {!deliveries.length && <p className="text-neutral-400 col-span-full text-center py-16">No delivery orders. Pull from Channels or take a delivery order in POS.</p>}
          </div>
        </TabsContent>
        <TabsContent value="riders">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {riders.map((r) => (
              <div key={r.id} className="kul-card p-4 flex items-center gap-3" data-testid={`rider-${r.id}`}>
                <div className="w-10 h-10 rounded-full bg-[var(--kul-primary)]/10 text-[var(--kul-primary)] flex items-center justify-center"><Bike className="w-5 h-5" /></div>
                <div className="flex-1"><div className="font-semibold">{r.name}</div><div className="text-xs text-neutral-400">{r.phone} · {r.vehicle}</div></div>
                <Badge className={r.status === "available" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-800"}>{r.status}</Badge>
                <button onClick={() => delRider(r)} className="text-neutral-300 hover:text-[var(--kul-danger)]"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {!riders.length && <p className="text-neutral-400 col-span-full text-center py-12">No riders yet.</p>}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>Add Rider</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rider-name" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="rider-phone" /></div>
            <div><Label>Vehicle</Label><Input value={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={addRider} className="bg-[var(--kul-primary)] text-white" data-testid="save-rider">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignFor} onOpenChange={(v) => { if (!v) setAssignFor(null); }}>
        <DialogContent><DialogHeader><DialogTitle>Assign rider · {assignFor?.order_number}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {riders.filter((r) => r.status === "available").map((r) => (
              <button key={r.id} onClick={() => assign(r.id)} data-testid={`pick-rider-${r.id}`}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-black/5 text-left">
                <Bike className="w-5 h-5 text-[var(--kul-primary)]" /><div><div className="font-medium">{r.name}</div><div className="text-xs text-neutral-400">{r.phone}</div></div>
              </button>
            ))}
            {!riders.filter((r) => r.status === "available").length && <p className="text-neutral-400 text-center py-6">No available riders. Add one first.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
